import { Box, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useComponents } from "../hooks/useComponents";
import { useComponentFilter } from "../hooks/useComponentFilter";
import { useComponentForm } from "../hooks/useComponentForm";
import { useAirframes } from "../../airframes/hooks/useAirframes";
import { ComponentFilterPane } from "./ComponentFilterPane";
import { ComponentGrid } from "./ComponentGrid";
import { ComponentFormDialog } from "../dialogs/ComponentFormDialog";
import { MiniPackAssemblyDrawer } from "../dialogs/MiniPackAssemblyDrawer";
import { ConfirmDialog } from "../../../common/confirm-dialog";
import { Component } from "../../../../types/component";
import { useOperatorService } from "../../../../api/operatorService";
import { useShopVisitReasonService } from "../../../../api/shopVisitReasonService";
import { useTenantDisplayPreferences } from "../../../../hooks/useTenantDisplayPreferences";
import { OffWingType } from "../../../../types/DetachComponent";
import { ShopVisitReasonItem } from "../../../../types/shopVisitReason";
import type { Operator } from "../../../../types/operator";
import { normalizeOperatorsFromApi } from "../../../../utils/operatorUtils";


export function ManageComponents() {
  const navigate = useNavigate();
  const airframeData = useAirframes(false);
  const data = useComponents(airframeData.airframes);
  const filter = useComponentFilter(
    data.components,
    data.attachmentListFilter,
    data.setAttachmentListFilter
  );
  const form = useComponentForm();
  const [miniPackComponent, setMiniPackComponent] = useState<Component | null>(null);
  const { getReasons } = useShopVisitReasonService();
  const [detachReasonOptions, setDetachReasonOptions] = useState<ShopVisitReasonItem[]>([]);

  const { getOperators } = useOperatorService();
  const [operators, setOperators] = useState<Operator[]>([]);
  const { preferences: displayPreferences } = useTenantDisplayPreferences();

  useEffect(() => {
    let active = true;
    void getOperators(true)
      .then((items) => {
        if (active) setOperators(normalizeOperatorsFromApi(items));
      })
      .catch(() => {
        if (active) setOperators([]);
      });
    return () => {
      active = false;
    };
  }, [getOperators]);

  useEffect(() => {
    const shouldLoadReasons =
      form.confirmDetachSubAssembly.open &&
      form.confirmDetachSubAssembly.step === "offWing" &&
      form.confirmDetachSubAssembly.offWingType === "In Shop";

    if (!shouldLoadReasons) {
      setDetachReasonOptions([]);
      return;
    }

    let active = true;
    void getReasons("LandingGear")
      .then((reasons) => {
        if (active) {
          setDetachReasonOptions(Array.isArray(reasons) ? reasons : []);
        }
      })
      .catch(() => {
        if (active) {
          setDetachReasonOptions([]);
        }
      });

    return () => {
      active = false;
    };
  }, [
    form.confirmDetachSubAssembly.open,
    form.confirmDetachSubAssembly.step,
    form.confirmDetachSubAssembly.offWingType,
    getReasons,
  ]);

  const handleEdit = useCallback(
    async (component: any) => {
      try {
        const fullComponent = await data.fetchById(component.id);
        if (fullComponent) {
          await form.edit(fullComponent);
        }
      } catch {
        // Error already handled in fetchById
      }
    },
    [data, form]
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ display: "flex", height: "100%", gap: 2, p: 2 }}>
        <ComponentFilterPane
          filter={filter.filter}
          onChange={filter.setFilter}
          attachmentFilter={data.attachmentListFilter}
          onAttachmentFilterChange={(v) => {
            data.setAttachmentListFilter(v);
            if (v === "attached") {
              filter.clearOffWingTypeFilters();
              filter.clearOperatorFilters();
            }
          }}
          offWingTypeFilters={filter.offWingTypeFilters}
          onOffWingTypeFilterToggle={filter.toggleOffWingTypeFilter}
          operators={operators}
          operatorFilters={filter.operatorFilters}
          onOperatorFilterToggle={filter.toggleOperatorFilter}
          showOperatorFilter={displayPreferences.showOperator}
          onCreate={form.create}
        />

        <ComponentGrid
          loading={data.loading}
          components={filter.filteredComponents}
          airframes={airframeData.airframes}
          onEdit={handleEdit}
          onDelete={data.remove}
          onMiniPack={(component) => setMiniPackComponent(component)}
          displayPreferences={displayPreferences}
        />

        <ComponentFormDialog
          open={form.open}
          form={form.form}
          isCreate={form.isCreate}
          change={form.change}
          changeMetadata={form.changeCustomField}
          close={form.close}
          displayPreferences={displayPreferences}
          operators={operators}
          hasSubAssembly={Boolean(form.form.hasSubAssembly)}
          formBaselineKey={form.formBaselineKey}
          onHasSubAssemblyChange={form.setHasSubAssembly}
          pendingSubAssemblies={form.form.pendingSubAssemblies ?? []}
          onAddPendingSubAssembly={form.addPendingSubAssembly}
          onRemovePendingSubAssembly={form.requestRemovePendingSubAssembly}
          onSave={async (values) => {
            const componentId = await data.save(values, form.selected?.id);
            if (componentId) {
              form.close();
            }
          }}
        />

        <ConfirmDialog
          open={form.confirmDetachSubAssembly.open && form.confirmDetachSubAssembly.step === "confirm"}
          title="Detach Sub-Assembly"
          message="Are you sure you want to detach this sub-assembly from the assembly?"
          confirmLabel="Detach"
          cancelLabel="Cancel"
          isDangerous
          onConfirm={form.handleConfirmDetachSubAssembly}
          onCancel={() => form.setConfirmDetachSubAssembly({
            open: false,
            step: "confirm",
            subAssemblyId: null,
            isPersisted: false,
            offWingType: "In Shop",
            shopVisitReasonId: null,
            shopVisitReasonLabel: null,
          })}
          isLoading={form.isDetachingSubAssembly}
        />

        <ConfirmDialog
          open={form.confirmDetachSubAssembly.open && form.confirmDetachSubAssembly.step === "offWing"}
          title="Detach Sub-Assembly"
          message={
            <Box sx={{ mt: 1 }}>
              <Typography sx={{ color: "#ccc", mb: 2 }}>
                Select the off-wing status for the detached sub-assembly.
              </Typography>
              <FormControl fullWidth required>
                <InputLabel
                  id="sub-assembly-off-wing-type-label"
                  sx={{
                    color: "#fff",
                    "&.Mui-focused": { color: "#fff" },
                    "&.MuiInputLabel-shrink": { color: "#fff" },
                  }}
                >
                  OffWing Type
                </InputLabel>
                <Select
                  labelId="sub-assembly-off-wing-type-label"
                  label="OffWing Type"
                  value={form.confirmDetachSubAssembly.offWingType}
                  onChange={(e) =>
                    form.setConfirmDetachSubAssembly({
                      ...form.confirmDetachSubAssembly,
                      offWingType: e.target.value as OffWingType,
                      shopVisitReasonId:
                        e.target.value === "In Shop"
                          ? form.confirmDetachSubAssembly.shopVisitReasonId
                          : null,
                      shopVisitReasonLabel:
                        e.target.value === "In Shop"
                          ? form.confirmDetachSubAssembly.shopVisitReasonLabel
                          : null,
                    })
                  }
                  sx={{
                    color: "#fff",
                    backgroundColor: "#1a1a1a",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "hsl(19 101% 51% / 1)",
                      borderWidth: 1,
                    },
                    "& .MuiSvgIcon-root": { color: "#fff" },
                  }}
                >
                  <MenuItem value="In Shop">In Shop</MenuItem>
                  <MenuItem value="Serviceable - Spare">Serviceable - Spare</MenuItem>
                  <MenuItem value="Unserviceable - Spare">Unserviceable - Spare</MenuItem>
                </Select>
              </FormControl>

              {form.confirmDetachSubAssembly.offWingType === "In Shop" && (
                <FormControl fullWidth required sx={{ mt: 2 }}>
                  <InputLabel
                    id="sub-assembly-in-shop-reason-label"
                    sx={{
                      color: "#fff",
                      "&.Mui-focused": { color: "#fff" },
                      "&.MuiInputLabel-shrink": { color: "#fff" },
                    }}
                  >
                    In Shop Reason
                  </InputLabel>
                  <Select
                    labelId="sub-assembly-in-shop-reason-label"
                    label="In Shop Reason"
                    value={form.confirmDetachSubAssembly.shopVisitReasonId || ""}
                    onChange={(e) => {
                      const selectedReason = detachReasonOptions.find(
                        (r) => r.id === e.target.value
                      );
                      form.setConfirmDetachSubAssembly({
                        ...form.confirmDetachSubAssembly,
                        shopVisitReasonId: String(e.target.value),
                        shopVisitReasonLabel: selectedReason?.label || null,
                      });
                    }}
                    sx={{
                      color: "#fff",
                      backgroundColor: "#1a1a1a",
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
                      "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "hsl(19 101% 51% / 1)",
                        borderWidth: 1,
                      },
                      "& .MuiSvgIcon-root": { color: "#fff" },
                    }}
                  >
                    {detachReasonOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        No reasons configured
                      </MenuItem>
                    )}
                    {detachReasonOptions.map((reason, index) => (
                      <MenuItem
                        key={`${String(reason.id)}-${reason.label}-${index}`}
                        value={reason.id}
                      >
                        {reason.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          }
          confirmLabel="Save"
          cancelLabel="Cancel"
          isDangerous={false}
          onConfirm={form.handleSaveDetachSubAssembly}
          onCancel={() => form.setConfirmDetachSubAssembly({
            open: false,
            step: "confirm",
            subAssemblyId: null,
            isPersisted: false,
            offWingType: "In Shop",
            shopVisitReasonId: null,
            shopVisitReasonLabel: null,
          })}
          isLoading={form.isDetachingSubAssembly}
        />

        <ConfirmDialog
          open={data.confirmDelete.open}
          title="Delete Component"
          message="Are you sure you want to delete this component?"
          confirmLabel="Delete"
          cancelLabel="Cancel"
          isDangerous={true}
          isLoading={data.isDeleting}
          onConfirm={data.handleConfirmDelete}
          onCancel={() => data.setConfirmDelete({ open: false, component: null })}
        />

        <MiniPackAssemblyDrawer
          open={Boolean(miniPackComponent)}
          component={miniPackComponent}
          onClose={() => setMiniPackComponent(null)}
          onOpenJobs={(component) => {
            const label = `${component.serialNumber || component.model || component.type || "Assembly"}`.trim();
            navigate(`/assets/mini-pack-generator?scope=assembly&label=${encodeURIComponent(label)}`);
          }}
        />
      </Box>
    </LocalizationProvider>
  );
}
